import React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { getQuestionsAndAnswers, selectAppraisal } from "../../store/selectors";
import useAssumptionsRadioAndCheckboxQuestion from "./useAssumptionsRadioAndCheckboxQuestion";
import { useAssumptionQuestions } from "./useAssumptionQuestions";

interface AnswerObject {
  id: number;
  label: string;
}

interface RadioAndCheckboxProps {
  question: Question;
  handleAssumptionRadioChange: (
    questionId: number | null,
    value: string | number,
    showCheckboxValue: number | undefined
  ) => void;
  handleCheckboxChange: (
    questionId: number | null,
    answerId: string | number,
    checked: boolean
  ) => void;
  checkboxState: any;
  selectedQuestionsHaveChanged?: any;
}

export const AssumptionsRadioAndCheckboxQuestion = ({
  question,
  handleAssumptionRadioChange,
  selectedQuestionsHaveChanged,
  handleCheckboxChange,
  checkboxState
}: RadioAndCheckboxProps) => {
  const questionsAndAnswers = useSelector(getQuestionsAndAnswers);
  const storedAnswer = questionsAndAnswers && questionsAndAnswers[question.id];
  const { conditionalAnswersSubmitted } = useSelector(selectAppraisal);

  const {
    noDamage,
    answersForCheckbox,
    groupName,
    questionString,
    noDamageLabel,
    showCheckboxLabel,
    showCheckboxValue,
    labelForCheckbox
  } = useAssumptionsRadioAndCheckboxQuestion({ question });

  const { questionHasChanged } = useAssumptionQuestions();
  const changedQuestionsKeys = Object.keys(selectedQuestionsHaveChanged);
  const checkBoxAnswerIds = answersForCheckbox.map((answer: Answer) => answer.id);

  const [toggleCheckbox, setToggleCheckbox] = useState(
    storedAnswer[0] === showCheckboxValue ||
      storedAnswer?.some((answerId: number) =>
        checkBoxAnswerIds.includes(answerId)
      )
      ? true
      : false
  );

  const isAnswerSubmitted = conditionalAnswersSubmitted.includes(question.id);
  const radioError = !storedAnswer && isAnswerSubmitted;
  const checkboxError =
    (checkboxState[question.id][0] === showCheckboxValue || checkboxState[question.id]?.length === 0) &&
    isAnswerSubmitted;
  return (
    <>
      <p className="ch-width--12 ch-form__control-label ch-mb--2">
        {questionString}
      </p>
      <div className="ch-form__group">
        {radioError && (
          <div className="ch-form__group ch-form__group--error ch-mb--0">
            <span className="ch-form__control-validation ch-mb--1">
              Please at least one option
            </span>
          </div>
        )}

        <input
          id={`${groupName}-yes`}
          type="radio"
          data-testid={`${groupName}-yes-radio`}
          name={`${groupName}NeedRepaired`}
          value={showCheckboxValue}
          className="ch-radio"
          checked={toggleCheckbox}
          onChange={({ target: { value } }) => {
            setToggleCheckbox(true);
            handleAssumptionRadioChange(
              question.id,
              parseInt(value),
              showCheckboxValue
            );
          }}
        />
        <label
          htmlFor={`${groupName}-yes`}
          className={`ch-radio__label ch-radio__label--compact`}
        >
          {showCheckboxLabel}
        </label>

        <input
          id={`${groupName}-no`}
          type="radio"
          name={`${groupName}NeedRepaired`}
          data-testid={`${groupName}-no-radio`}
          value={noDamage}
          className="ch-radio"
          checked={!toggleCheckbox}
          onChange={({ target: { value } }) => {
            setToggleCheckbox(false);
            handleAssumptionRadioChange(
              question.id,
              parseInt(value),
              showCheckboxValue
            );
          }}
        />
        <label
          htmlFor={`${groupName}-no`}
          className={`ch-radio__label ch-radio__label--compact ch-ml--2`}
        >
          {noDamageLabel}
        </label>
      </div>
      {toggleCheckbox && (
        <div
          className={`ch-form__group ${
            checkboxError ? "ch-form__group--error" : ""
          }`}
        >
          <label
            htmlFor={`${groupName}Damaged`}
            className="ch-form__control-label"
          >
            {labelForCheckbox}
          </label>
          {checkboxError && (
            <span className="ch-form__control-validation  ch-mt--1">
              Please choose at least one option
            </span>
          )}
          <div
            className="ch-form__group ch-display--flex"
            style={{ flexDirection: "column" }}
          >
            {answersForCheckbox.map((answer: AnswerObject) => (
              <div key={answer.id}>
                <input
                  className="ch-checkbox"
                  id={answer.id.toString()}
                  name={answer.id.toString()}
                  type="checkbox"
                  value={answer.id}
                  checked={
                    questionHasChanged(question, changedQuestionsKeys)
                      ? checkboxState[question.id]?.includes(answer.id)
                      : questionsAndAnswers[question.id]?.includes(answer.id)
                  }
                  onChange={({ target: { value, checked } }) => {
                    handleCheckboxChange(question.id, parseInt(value), checked);
                  }}
                />
                <label
                  htmlFor={answer.id.toString()}
                  className="ch-checkbox__label ch-fs--3 ch-fw--400 ch-mt--2"
                >
                  {answer.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
